import React from 'react';

interface SimpleMarkdownRendererProps {
    content?: string;
    className?: string;
    emptyText?: string;
}

const renderInline = (text: string): React.ReactNode[] => {
    const inlineTokenPattern = /(\[[^\]\n]+\]\((?:(?:https?:\/\/|mailto:|\/)[^)]+)\)|\*\*[^*\n]+?\*\*|__[^_\n]+?__|<u>[^<\n]+?<\/u>|`[^`\n]+?`|\*[^*\n]+?\*|_[^_\n]+?_)/g;

    return text
        .split(inlineTokenPattern)
        .filter(Boolean)
        .map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
                return <strong key={`${part}-${index}`} className="font-semibold">{part.slice(2, -2)}</strong>;
            }

            if (part.startsWith('__') && part.endsWith('__') && part.length > 4) {
                return <strong key={`${part}-${index}`} className="font-semibold">{part.slice(2, -2)}</strong>;
            }

            if (part.startsWith('<u>') && part.endsWith('</u>') && part.length > 7) {
                return <span key={`${part}-${index}`} className="underline underline-offset-2">{part.slice(3, -4)}</span>;
            }

            if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
                return (
                    <code key={`${part}-${index}`} className="rounded bg-gray-900/5 px-1 py-0.5 font-mono text-[0.9em] text-current">
                        {part.slice(1, -1)}
                    </code>
                );
            }

            if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                return <em key={`${part}-${index}`} className="italic">{part.slice(1, -1)}</em>;
            }

            if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
                return <em key={`${part}-${index}`} className="italic">{part.slice(1, -1)}</em>;
            }

            const linkMatch = part.match(/^\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:|\/)[^)]+)\)$/);
            if (linkMatch) {
                const [, label, href] = linkMatch;
                return (
                    <a
                        key={`${part}-${index}`}
                        href={href}
                        target={href.startsWith('http') ? '_blank' : undefined}
                        rel={href.startsWith('http') ? 'noreferrer' : undefined}
                        className="font-medium text-blue-600 underline underline-offset-2"
                    >
                        {label}
                    </a>
                );
            }

            return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
        });
};

export const SimpleMarkdownRenderer: React.FC<SimpleMarkdownRendererProps> = ({
    content,
    className = '',
    emptyText = '暂无内容'
}) => {
    const normalized = (content || '').replace(/\r\n/g, '\n').trim();

    if (!normalized) {
        return <p className={`text-sm leading-relaxed text-gray-500 ${className}`}>{emptyText}</p>;
    }

    const lines = normalized.split('\n');
    const blocks: React.ReactNode[] = [];
    let index = 0;

    while (index < lines.length) {
        const line = lines[index].trim();

        if (!line) {
            index += 1;
            continue;
        }

        if (line.startsWith('```')) {
            const codeLines: string[] = [];
            index += 1;

            while (index < lines.length && !lines[index].trim().startsWith('```')) {
                codeLines.push(lines[index]);
                index += 1;
            }

            if (index < lines.length && lines[index].trim().startsWith('```')) {
                index += 1;
            }

            blocks.push(
                <pre key={`code-${index}`} className="overflow-x-auto rounded-lg bg-gray-900/5 p-3 text-sm leading-relaxed text-gray-800">
                    <code className="font-mono">{codeLines.join('\n')}</code>
                </pre>
            );
            continue;
        }

        if (/^#{1,3}\s+/.test(line)) {
            const [, markers, content] = line.match(/^(#{1,3})\s+(.*)$/) || [];
            const headingClass = markers?.length === 1
                ? 'text-base font-bold text-gray-900 pt-1'
                : markers?.length === 2
                    ? 'text-sm font-bold text-gray-900 pt-1'
                    : 'text-xs font-bold text-gray-700 uppercase tracking-wide pt-1';

            blocks.push(
                <h3 key={`heading-${index}`} className={headingClass}>
                    {renderInline((content || '').trim())}
                </h3>
            );
            index += 1;
            continue;
        }

        if (/^[-*+]\s+/.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^[-*+]\s+/.test(lines[index].trim())) {
                items.push(lines[index].trim().replace(/^[-*+]\s+/, ''));
                index += 1;
            }

            blocks.push(
                <ul key={`ul-${index}`} className="list-disc pl-5 space-y-1 text-sm leading-relaxed text-gray-700">
                    {items.map((item, itemIndex) => (
                        <li key={`${item}-${itemIndex}`}>{renderInline(item)}</li>
                    ))}
                </ul>
            );
            continue;
        }

        if (/^\d+\.\s+/.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
                items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
                index += 1;
            }

            blocks.push(
                <ol key={`ol-${index}`} className="list-decimal pl-5 space-y-1 text-sm leading-relaxed text-gray-700">
                    {items.map((item, itemIndex) => (
                        <li key={`${item}-${itemIndex}`}>{renderInline(item)}</li>
                    ))}
                </ol>
            );
            continue;
        }

        if (/^>\s*/.test(line)) {
            const quoteLines: string[] = [];
            while (index < lines.length && /^>\s*/.test(lines[index].trim())) {
                quoteLines.push(lines[index].trim().replace(/^>\s*/, ''));
                index += 1;
            }

            blocks.push(
                <blockquote key={`quote-${index}`} className="rounded-lg bg-gray-900/5 px-4 py-3 text-sm leading-relaxed text-gray-700">
                    {renderInline(quoteLines.join(' '))}
                </blockquote>
            );
            continue;
        }

        const paragraphLines: string[] = [];
        while (
            index < lines.length &&
            lines[index].trim() &&
            !lines[index].trim().startsWith('```') &&
            !/^#{1,3}\s+/.test(lines[index].trim()) &&
            !/^[-*+]\s+/.test(lines[index].trim()) &&
            !/^\d+\.\s+/.test(lines[index].trim()) &&
            !/^>\s*/.test(lines[index].trim())
        ) {
            paragraphLines.push(lines[index].trim());
            index += 1;
        }

        blocks.push(
            <p key={`p-${index}`} className="text-sm leading-relaxed text-gray-700">
                {renderInline(paragraphLines.join(' '))}
            </p>
        );
    }

    return <div className={`space-y-3 ${className}`}>{blocks}</div>;
};

export default SimpleMarkdownRenderer;
